import {useCallback, useEffect, useRef} from 'react';
import {Configure, InstantSearch, type InstantSearchProps} from 'react-instantsearch';
import {history as historyRouter} from 'instantsearch.js/es/lib/routers/index.js';
import {Pagination} from "@/instantsearch/components/Pagination";
import {FilterBoxToggle} from "@/instantsearch/components/FilterBoxToggle";
import {SearchHeader} from "@/instantsearch/components/SearchHeader";
import {useInstantSearchUIContext} from "@/instantsearch/InstantSearchUIProvider";
import {ScrollTo} from "@/instantsearch/components/ScrollTo";
import {NoResults, NoResultsBoundary} from "@/instantsearch/components/NoResultsBoundary";
import {ItemList} from "@/instantsearch/components/ItemList";
import {SidebarMenu} from "@/instantsearch/components/SidebarMenu";
import {FundingCampaignFilter} from "@/instantsearch/components/FundingCampaignFilter";
import {FundingSearchBox} from "@/instantsearch/components/FundingSearchBox";
import {useIntersectionObserver} from "@/utils/useIntersectionObserver";
import {seoFriendlyStateMapping} from "@/instantsearch/seoFriendlyStateMapping";
import {useConfigContext} from "@/ConfigProvider";
import type { AppRouteState } from '../types/types';
import {Sidebar} from "@/instantsearch/components/Sidebar";
import {FilterArea} from "@/instantsearch/components/FilterArea";
import {Stats} from "@/instantsearch/components/Stats";
import {SortBy} from "@/instantsearch/components/SortBy";
import {AnalyticsProvider} from "@/providers/AnalyticsProvider";

function urlToLocation(u: URL): Location {
    const locLike = {
        href: u.href,
        origin: u.origin,
        protocol: u.protocol,
        host: u.host,
        hostname: u.hostname,
        port: u.port,
        pathname: u.pathname,
        search: u.search,
        hash: u.hash,
        assign: () => {},
        reload: () => {},
        replace: () => {},
        ancestorOrigins: { length: 0, item: () => null, contains: () => false } as any,
    } as any;

    return locLike as unknown as Location;
}

export function AppInstantsearch({ serverUrl }: { serverUrl?: URL }) {
    const searchRef = useRef<HTMLDivElement>(null);
    const {
        algoliaConfig: {algoliaIndexName, filterPositionLeft,algoliaShopFilter,itemsPerPage,isFundingPage},
        algoliaRefinements: {refinementList},
        searchClient,
        sorting
    } = useConfigContext();
    const headerRef = useRef<HTMLElement>(null);
    useIntersectionObserver(headerRef, 'is-sticky');
    const {isActive, viewMode, setViewMode} = useInstantSearchUIContext();
    const onStateChange = useCallback<NonNullable<InstantSearchProps['onStateChange']>>(
        ({uiState, setUiState}) => {
            setUiState(uiState);
        },
        []
    );
    const _itemsPerPage=itemsPerPage>0?itemsPerPage:33;
    const getInitialUiState = useCallback(() => {
        const baseState = { [algoliaIndexName]: { hitsPerPage: _itemsPerPage } };

        if (typeof window === 'undefined') return baseState;

        const params = new URLSearchParams(window.location.search);
        const refinements: Record<string, string[]> = {};

        refinementList.forEach(({ name }) => {
            const key = name.split(".")[1]?.toLowerCase();
            if (key && params.has(key)) {
                const value = params.get(key);
                if (value) {
                    refinements[name] = value.split("|");
                }
            }
        });

        const uiState: any = { ...baseState[algoliaIndexName] };

        if (Object.keys(refinements).length > 0) {
            uiState.refinementList = refinements;
        }

        if (params.has('page')) {
            uiState.page = parseInt(params.get('page')!);
        }

        if (params.has('sort_by')) {
            const sortCode = params.get('sort_by');
            const found = sorting.find(x => x.code === sortCode);
            if (found) uiState.sortBy = found.value;
        }

        return { [algoliaIndexName]: uiState };
    }, [algoliaIndexName, refinementList, sorting]);

    const router = historyRouter<AppRouteState>({
        getLocation: () =>
            typeof window === 'undefined'
                ? urlToLocation(serverUrl!)
                : window.location,
    });

    useEffect(() => {
        setTimeout(() => {
            const dataItemNo = window.location.hash.slice(1);
            if (dataItemNo) {
                const targetElement = document.querySelector(`[data-itemno="${dataItemNo}"]`);
                if (targetElement) {
                    targetElement.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                    targetElement.classList.add("highlight");
                    setTimeout(() => {
                        targetElement.classList.remove("highlight");
                    }, 3000);
                }
            }
        }, 900)
    }, []);
    if (filterPositionLeft) {
        return (
            <AnalyticsProvider>
            <InstantSearch
                searchClient={searchClient}
                indexName={algoliaIndexName}
                onStateChange={onStateChange}
                insights={true}
                routing={{
                    router,
                    stateMapping: seoFriendlyStateMapping(sorting, algoliaIndexName, refinementList),
                }}
                initialUiState={getInitialUiState()}
                future={{
                    preserveSharedStateOnUnmount: true,
                }}
            >
                <Configure
                    filters={algoliaShopFilter ?? undefined}
                    maxValuesPerFacet={100}
                    hitsPerPage={_itemsPerPage}

                />
                <ScrollTo>
                    <SearchHeader/>
                    <div className={`algoliaSearchLayout algoliaSearchLayout--filterPositionLeft`} ref={searchRef}>
                        <header className={"algoliaSearchLayout__header"} ref={headerRef}>
                            {refinementList.length > 0 && (
                                <div className={"algoliaSearchLayout__filterToggle"}>
                                    <FilterBoxToggle/>
                                </div>
                            )}
                            {!isFundingPage && (
                                <div className={"algoliaSearchLayout__view"}>
                                    <button
                                        className={`algoliaSearchLayout__viewToggle ${viewMode === 'tile' ? 'is-active' : ''}`}
                                        onClick={() => setViewMode('tile')}
                                        aria-label="Kachelansicht"
                                        aria-pressed={viewMode === 'tile'}
                                    >
                                        <i className="icon icon-columns" aria-hidden={true}></i>
                                    </button>
                                    <button
                                        className={`algoliaSearchLayout__viewToggle ${viewMode === 'list' ? 'is-active' : ''}`}
                                        onClick={() => setViewMode('list')}
                                        aria-label="Listenansicht"
                                        aria-pressed={viewMode === 'list'}
                                    >
                                        <i className="icon icon-list" aria-hidden={true}></i>
                                    </button>
                                </div>
                            )}
                            <div className={"algoliaSearchLayout__sorting"}>
                                <Stats/>
                                <SortBy items={sorting}/>
                            </div>
                        </header>
                        <section className={"algoliaSearchLayout__section"}>
                            <Sidebar>
                                <FilterArea />
                            </Sidebar>
                            <div className={`algoliaSearchLayout__main ${(isActive ? "is-active" : "")}`}>
                                <NoResultsBoundary fallback={<NoResults/>}>
                                <ItemList/>
                                    <div className="algoliaSearchLayout__pagination mt--large">
                                        <Pagination padding={1}/>
                                    </div>
                                </NoResultsBoundary>
                            </div>
                        </section>
                    </div>
                </ScrollTo>
            </InstantSearch>
            </AnalyticsProvider>
        );
    } else {
        return (
            <AnalyticsProvider>
            <InstantSearch
                searchClient={searchClient}
                indexName={algoliaIndexName}
                onStateChange={onStateChange}
                insights={true}
                routing={{
                    router,
                    stateMapping: seoFriendlyStateMapping(sorting, algoliaIndexName, refinementList),
                }}
                initialUiState={getInitialUiState()}
                future={{
                    preserveSharedStateOnUnmount: true,
                }}
            >
                <Configure
                    filters={isFundingPage ? undefined : (algoliaShopFilter ?? undefined)}
                    maxValuesPerFacet={100}
                    hitsPerPage={_itemsPerPage}
                />
                <ScrollTo>
                    <SearchHeader/>
                    {isFundingPage && <FundingCampaignFilter/>}
                    {isFundingPage && <FundingSearchBox/>}
                    <div className={`algoliaSearchLayout`} ref={searchRef}>
                        <NoResultsBoundary fallback={<NoResults/>}>
                            <header className={"algoliaSearchLayout__header"} ref={headerRef}>
                                {refinementList.length > 0 && (
                                    <>
                                        <div className={"algoliaSearchLayout__filterToggle"}>
                                            <FilterBoxToggle/>
                                        </div>
                                        <div className={"algoliaSearchLayout__headerControls"}>
                                            {!isFundingPage && (
                                                <div className={"algoliaSearchLayout__view"}>
                                                    <button
                                                        className={`algoliaSearchLayout__viewToggle ${viewMode === 'tile' ? 'is-active' : ''}`}
                                                        onClick={() => setViewMode('tile')}
                                                        aria-label="Kachelansicht"
                                                        aria-pressed={viewMode === 'tile'}
                                                    >
                                                        <i className="icon icon-columns" aria-hidden={true}></i>
                                                    </button>
                                                    <button
                                                        className={`algoliaSearchLayout__viewToggle ${viewMode === 'list' ? 'is-active' : ''}`}
                                                        onClick={() => setViewMode('list')}
                                                        aria-label="Listenansicht"
                                                        aria-pressed={viewMode === 'list'}
                                                    >
                                                        <i className="icon icon-list" aria-hidden={true}></i>
                                                    </button>
                                                </div>
                                            )}
                                            <div className={"algoliaSearchLayout__sorting"}>
                                                <SortBy items={sorting}/>
                                            </div>
                                        </div>
                                        {!isFundingPage && <SidebarMenu controls={
                                            <>
                                                <div className={"algoliaSearchLayout__view"}>
                                                    <button
                                                        className={`algoliaSearchLayout__viewToggle ${viewMode === 'tile' ? 'is-active' : ''}`}
                                                        onClick={() => setViewMode('tile')}
                                                        aria-label="Kachelansicht"
                                                        aria-pressed={viewMode === 'tile'}
                                                    >
                                                        <i className="icon icon-columns" aria-hidden={true}></i>
                                                    </button>
                                                    <button
                                                        className={`algoliaSearchLayout__viewToggle ${viewMode === 'list' ? 'is-active' : ''}`}
                                                        onClick={() => setViewMode('list')}
                                                        aria-label="Listenansicht"
                                                        aria-pressed={viewMode === 'list'}
                                                    >
                                                        <i className="icon icon-list" aria-hidden={true}></i>
                                                    </button>
                                                </div>
                                                <div className={"algoliaSearchLayout__sorting"}>
                                                    <SortBy items={sorting}/>
                                                </div>
                                            </>
                                        }/>}
                                    </>
                                )}
                            </header>
                            <section className={"algoliaSearchLayout__section"}>
                                <div className={`algoliaSearchLayout__main ${(isActive ? "is-active" : "")}`}>
                                    <ItemList/>
                                    <div className="algoliaSearchLayout__pagination mt--large">
                                        <Pagination padding={1}/>
                                    </div>
                                </div>
                            </section>
                        </NoResultsBoundary>
                    </div>
                </ScrollTo>
            </InstantSearch>
            </AnalyticsProvider>
        );
    }
}